import React, { useState, useRef, useEffect, useMemo } from "react";
import { CaretDown, Check, MagnifyingGlass } from "@phosphor-icons/react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { searchKey, normQ } from "@/lib/itemSort";

const fmt = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const meta = (m, showStock) => {
  const parts = [];
  if (m.sale_price > 0 && !String(m.name).includes("₹")) parts.push(`₹${fmt(m.sale_price)}/${m.unit}`);
  if (showStock && m.current_stock != null && !(m.item_type === "SERVICE" || m.is_service)) parts.push(`${fmt(m.current_stock)} ${m.unit}`);
  return parts.join(" · ");
};

/**
 * Searchable item picker on Radix Popover (works inside Dialogs: focus + Esc stay local).
 * Type to filter by name / thickness / gauge ("2.5 mm" = "2.5mm"); ↑ ↓ Enter Esc; group headings stay.
 */
export default function ItemPicker({
  value, onChange, items, placeholder = "Select item…", fallbackLabel = "", testid,
  loading = false, error = "", onRetry, showStock = true,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const inRef = useRef(null);
  const listRef = useRef(null);

  const selected = items.find((i) => i.id === value);
  const label = selected ? `${selected.name}${meta(selected, false) ? ` — ${meta(selected, false)}` : ""}` : (fallbackLabel || placeholder);
  const shown = useMemo(() => {
    const k = normQ(q);
    return k ? items.filter((m) => searchKey(m).includes(k)) : items;
  }, [items, q]);

  useEffect(() => { setHi(0); }, [q]);
  useEffect(() => { listRef.current?.querySelector(`[data-idx="${hi}"]`)?.scrollIntoView({ block: "nearest" }); }, [hi]);

  const openWith = (txt = "") => { setQ(txt); setHi(0); setOpen(true); };
  const pick = (m) => { onChange(m.id); setOpen(false); setQ(""); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, shown.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (shown[hi]) pick(shown[hi]); }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild>
        <button type="button"
          onKeyDown={(e) => {
            if (open) return;
            if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && e.key !== " ") { e.preventDefault(); openWith(e.key); }
            else if (e.key === "ArrowDown") { e.preventDefault(); openWith(); }
          }}
          className={"flex w-full items-center justify-between gap-2 h-9 rounded-sm border border-slate-600 bg-slate-800/60 px-3 text-sm text-left transition-colors hover:border-slate-500 " + ((selected || fallbackLabel) ? "text-white" : "text-slate-400")}
          data-testid={testid}>
          <span className="truncate">{label}</span>
          <CaretDown size={14} className={"shrink-0 opacity-70 transition-transform " + (open ? "rotate-180" : "")} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={4}
        onOpenAutoFocus={(e) => { e.preventDefault(); setTimeout(() => inRef.current?.focus(), 0); }}
        className="p-0 rounded-sm border border-slate-600 bg-slate-900 text-white shadow-xl w-[var(--radix-popover-trigger-width)] min-w-[260px] max-w-[min(420px,calc(100vw-8px))] z-[9999]">
        <div className="flex items-center gap-2 px-2 py-1.5 border-b border-slate-700">
          <MagnifyingGlass size={14} className="text-slate-400 shrink-0" />
          <input ref={inRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
            placeholder="Search: 2mm, 2.5, 16g, lock, golden…" className="flex-1 min-w-0 bg-transparent text-sm text-white outline-none h-8"
            data-testid={testid ? `${testid}-search` : "item-picker-search"} />
        </div>
        <div ref={listRef} className="max-h-72 overflow-y-auto" role="listbox" data-testid={testid ? `${testid}-listbox` : undefined}
          onWheel={(e) => e.stopPropagation()}>
          {error ? (
            <div className="text-red-300 text-xs px-3 py-4 text-center" data-testid={testid ? `${testid}-error` : undefined}>
              {error}{onRetry && <button type="button" onClick={onRetry} className="ml-2 px-2 py-0.5 border border-red-500/50 rounded-sm" data-testid={testid ? `${testid}-retry` : undefined}>RETRY</button>}
            </div>
          ) : loading && !items.length ? (
            <div className="text-slate-400 text-xs px-3 py-4 text-center">Loading items…</div>
          ) : shown.length === 0 ? (
            <div className="text-slate-500 text-xs px-3 py-4 text-center" data-testid={testid ? `${testid}-empty` : undefined}>{items.length ? "Koi item match nahi hua" : "No items yet. Add items in the Items page."}</div>
          ) : shown.map((m, i) => {
            const isSel = m.id === value;
            const head = m.group && m.group !== shown[i - 1]?.group;
            const mt = meta(m, showStock);
            return (
              <React.Fragment key={m.id}>
                {head && <div className="sticky top-0 px-3 pt-2 pb-1 text-[10px] font-mono-num tracking-widest text-orange-300/80 bg-slate-950" data-testid={testid ? `${testid}-group-${m.group}` : undefined}>{m.group}</div>}
                <button type="button" data-idx={i} onMouseEnter={() => setHi(i)} onClick={() => pick(m)} role="option" aria-selected={isSel}
                  className={"w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-white " + (i === hi ? "bg-orange-500/25" : isSel ? "bg-orange-500/15" : "hover:bg-orange-500/15")}
                  data-testid={testid ? `${testid}-option-${m.id}` : undefined}>
                  <span className="flex-1 min-w-0 truncate">{m.name}{mt && <span className="text-slate-400 font-mono-num text-xs"> — {mt}</span>}</span>
                  {isSel && <Check size={14} weight="bold" className="text-orange-400 shrink-0" />}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
